import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Star, Heart, ThumbsUp, MessageCircle } from "lucide-react";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFeedbackSubmit: (feedback: {
    rating: number;
    category: string;
    message: string;
  }) => void;
}

const FeedbackModal = ({
  isOpen,
  onClose,
  onFeedbackSubmit,
}: FeedbackModalProps) => {
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) return;

    setIsSubmitting(true);
    try {
      await onFeedbackSubmit({
        rating,
        category,
        message,
      });

      // Reset form
      setRating(0);
      setCategory("");
      setMessage("");
      onClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md mx-auto max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center space-y-2">
          <DialogTitle className="flex items-center justify-center gap-2 text-lg sm:text-xl">
            <Heart className="h-5 w-5 text-red-500" />
            How was your design experience?
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base text-center">
            Your feedback helps us improve the Kente design platform for
            everyone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Rating */}
          <div className="text-center">
            <label className="text-sm font-medium mb-3 block">
              Rate your experience
            </label>
            <div className="flex gap-2 sm:gap-3 justify-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="p-2 hover:scale-110 transition-transform touch-manipulation"
                  aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                >
                  <Star
                    className={`h-7 w-7 sm:h-8 sm:w-8 ${
                      star <= rating
                        ? "text-yellow-400 fill-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium mb-3 block text-center">
              What would you like to share?
            </label>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[
                {
                  id: "love",
                  label: "I love it!",
                  icon: Heart,
                  color: "text-red-500",
                },
                {
                  id: "improve",
                  label: "Suggestions",
                  icon: MessageCircle,
                  color: "text-blue-500",
                },
                {
                  id: "bug",
                  label: "Found a bug",
                  icon: ThumbsUp,
                  color: "text-orange-500",
                },
                {
                  id: "feature",
                  label: "New feature",
                  icon: Star,
                  color: "text-purple-500",
                },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setCategory(item.id)}
                  className={`p-3 sm:p-4 rounded-lg border-2 transition-all touch-manipulation min-h-[80px] sm:min-h-[90px] ${
                    category === item.id
                      ? "border-primary bg-primary/10"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <item.icon
                    className={`h-5 w-5 sm:h-6 sm:w-6 ${item.color} mx-auto mb-2`}
                  />
                  <span className="text-xs sm:text-sm font-medium block text-center">
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Tell us more (optional)
            </label>
            <Textarea
              placeholder="Share your thoughts, suggestions, or experience..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[100px] sm:min-h-[120px] text-sm sm:text-base resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-2">
            <Button
              onClick={handleSubmit}
              className="w-full h-12 text-sm font-medium"
              disabled={rating === 0 || isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Feedback"}
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="w-full h-10 text-sm"
              disabled={isSubmitting}
            >
              Skip for now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
